import React from "react";
import { useColors } from "../constants/Colors";

interface PageLayoutProps {
  /** Page title displayed in the header */
  title: string;
  /** Optional button element to display in the header (right side of title) */
  headerButton?: React.ReactNode;
  /** Optional content below the title row (e.g., tabs, filters) */
  headerContent?: React.ReactNode;
  /** Content that will be scrollable */
  children: React.ReactNode;
  /** Maximum width of the content area (default: max-w-4xl) */
  maxWidth?: string;
}

/**
 * A reusable page layout component with a fixed header and scrollable content area.
 * The header contains a title and an optional button.
 */
const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  headerButton,
  headerContent,
  children,
  maxWidth = "max-w-4xl",
}) => {
  const colors = useColors();

  return (
    <div
      className="h-full min-h-0 flex flex-col min-w-0 max-w-full overflow-hidden"
      style={{ backgroundColor: colors.containerNeutralSolidSurface }}
    >
      {/* Fixed Header */}
      <div
        className="flex-shrink-0 px-4 md:px-6 pt-7 pb-3 overflow-hidden"
        style={{
          backgroundColor: colors.containerNeutralSolidSurface,
        }}
      >
        <div className={`${maxWidth} mx-auto w-full min-w-0`}>
          <div className="flex items-center justify-between gap-4 min-w-0">
            <h1
              className="text-2xl font-gnarly-semibold m-0 leading-none tracking-tight"
              style={{ color: colors.textNeutralDefault }}
            >
              {title}
            </h1>
            {headerButton && (
              <div className="flex-shrink-0">{headerButton}</div>
            )}
          </div>
          {headerContent && (
            <div className="flex items-center mt-3 min-w-0 overflow-hidden">{headerContent}</div>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden px-4 md:px-6 pt-4 pb-6 flex flex-col"
        style={{ scrollbarGutter: "stable" }}
      >
        <div className={`${maxWidth} mx-auto w-full min-w-0 flex-1 flex flex-col`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default PageLayout;
